import { Modal } from "tailwindcss-stimulus-components";

export default class MissionTaskModal extends Modal {
  static targets = ["dialog", "frame", "prevButton", "nextButton"];

  connect() {
    super.connect();
    this.missionTaskIds = [];
    this.currentMissionTaskIndex = -1;
    this.handleKeyDownBound = this.handleKeyDown.bind(this);
    this.handleEscapeBound = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(event);
      }
    };
    if (this.hasDialogTarget) {
      this.dialogTarget.addEventListener('keydown', this.handleKeyDownBound);
      this.dialogTarget.addEventListener('keydown', this.handleEscapeBound);
    }
  }

  disconnect() {
    if (this.hasDialogTarget) {
      this.dialogTarget.removeEventListener('keydown', this.handleKeyDownBound);
      this.dialogTarget.removeEventListener('keydown', this.handleEscapeBound);
    }
    super.disconnect();
  }

  getMissionTaskIdFromEvent(event) {
    if (!event?.target) return null;
    let missionTaskId = event.target.dataset.missionTaskId;
    if (!missionTaskId) {
      const parent = event.target.closest('[data-mission-task-id]');
      missionTaskId = parent ? parent.dataset.missionTaskId : null;
    }
    console.log("Mission Task ID from event:", missionTaskId);
    return missionTaskId;
  }

  highlightCurrentMissionTask() {
    // Remove highlight from all
    document.querySelectorAll('.border-sapphire').forEach(el => {
      el.classList.remove('border-2','border-sapphire');
    });
    // Highlight the current
    const id = this.missionTaskIds[this.currentMissionTaskIndex];
    if (!id) return;
    const el = document.querySelector(`[data-mission-task-id='${id}']`);
    if (el) {
      el.classList.add('border-sapphire', 'border-2');
    }
  }

  open(event) {
    super.open(event);
    // Collect all mission task IDs in the current view
    this.missionTaskIds = Array.from(document.querySelectorAll('[data-mission-task-id]'))
      .map(el => el.dataset.missionTaskId)
      .filter(Boolean);
    const missionTaskId = this.getMissionTaskIdFromEvent(event);
    this.currentMissionTaskIndex = this.missionTaskIds.indexOf(missionTaskId);
    if (missionTaskId) {
      this.triggerTurboFrameReload(missionTaskId);
      localStorage.setItem("openModalMissionTaskId", missionTaskId);
    }
    // Listen for turbo:frame-load to update nav buttons after frame loads
    if (this.hasFrameTarget) {
      this.frameTarget.addEventListener('turbo:frame-load', this.handleFrameLoad);
    }
    this.highlightCurrentMissionTask();
    this.updateNavButtons();
    // Focus dialog for keyboard events
    if (this.hasDialogTarget) {
      this.dialogTarget.focus();
    }
  }

  handleFrameLoad = () => {
    this.updateNavButtons();
    this.highlightCurrentMissionTask();
  }

  close(event) {
    // prevent default close behavior so a page refresh doesn't happen
    event?.preventDefault();
    super.close(event);
    // Scroll the last previewed record into view
    const id = this.missionTaskIds[this.currentMissionTaskIndex];
    if (id) {
      const el = document.querySelector(`[data-mission-task-id='${id}']`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    // Always clear the openModalMissionTaskId from localStorage
    localStorage.removeItem("openModalMissionTaskId");
    if (this.hasFrameTarget) {
      this.frameTarget.removeEventListener('turbo:frame-load', this.handleFrameLoad);
    }
  }

  triggerTurboFrameReload(missionTaskId) {
    const mission = document.querySelector('[data-mission-id]');
    const missionId = mission ? mission.dataset.missionId : null;
    if (missionId && missionTaskId) {
      const url = `/missions/${missionId}/tasks/${missionTaskId}/preview?frame=${this.frameTarget.id}`;
      this.frameTarget.setAttribute('src', url);
    } else {
      console.error("Missing mission ID or mission task ID", { missionId, missionTaskId });
    }
  }

  refreshVisibleMissionTaskIds() {
    // Get the current mission task ID before refreshing the list
    const currentMissionTaskId = this.missionTaskIds[this.currentMissionTaskIndex];

    // Refresh the list to only include visible cards
    this.missionTaskIds = Array.from(document.querySelectorAll('[data-mission-task-id]'))
      .filter(card => card.style.display !== 'none') // Only include visible cards
      .map(el => el.dataset.missionTaskId)
      .filter(Boolean);

    // Update the current index to match the current mission task ID in the new list
    this.currentMissionTaskIndex = this.missionTaskIds.indexOf(currentMissionTaskId);

    // If the current mission task is no longer visible, we might need to handle this case
    if (this.currentMissionTaskIndex === -1 && this.missionTaskIds.length > 0) {
      this.currentMissionTaskIndex = 0; // Default to first visible item
    }
  }

  navigateToMissionTask(index) {
    if (index < 0 || index >= this.missionTaskIds.length) return;
    const missionTaskId = this.missionTaskIds[index];
    this.triggerTurboFrameReload(missionTaskId);
    localStorage.setItem("openModalMissionTaskId", missionTaskId);
    this.currentMissionTaskIndex = index;
    this.updateNavButtons();
    this.highlightCurrentMissionTask();
  }

  flashChevron(direction) {
    let btn = null;
    if (direction === 'left' && this.hasPrevButtonTarget) {
      btn = this.prevButtonTarget;
    } else if (direction === 'right' && this.hasNextButtonTarget) {
      btn = this.nextButtonTarget;
    }
    if (btn) {
      // Save original classes
      const originalClasses = btn.getAttribute('data-original-classes') || btn.className;
      btn.setAttribute('data-original-classes', originalClasses);
      btn.classList.remove('bg-white/80', 'text-gray-700', 'hover:bg-white');
      btn.classList.add('bg-sapphire', 'text-white');
      setTimeout(() => {
        btn.className = btn.getAttribute('data-original-classes');
      }, 180);
      // Force a reflow to ensure the class changes are applied before the timeout
      void btn.offsetWidth;
    }
  }

  prev() {
    this.flashChevron('left');

    // Refresh the visible mission task IDs to account for any filtering changes
    this.refreshVisibleMissionTaskIds();

    let i = this.currentMissionTaskIndex - 1;
    const currentId = this.missionTaskIds[this.currentMissionTaskIndex];
    while (i >= 0 && this.missionTaskIds[i] === currentId) {
      i--;
    }
    this.navigateToMissionTask(i);
  }

  next() {
    this.flashChevron('right');

    // Refresh the visible mission task IDs to account for any filtering changes
    this.refreshVisibleMissionTaskIds();

    let i = this.currentMissionTaskIndex + 1;
    const currentId = this.missionTaskIds[this.currentMissionTaskIndex];
    while (i < this.missionTaskIds.length && this.missionTaskIds[i] === currentId) {
      i++;
    }
    this.navigateToMissionTask(i);
  }

  handleKeyDown(event) {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.prev();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      this.next();
    }
  }

  updateNavButtons() {
    if (this.hasPrevButtonTarget) {
      if (this.currentMissionTaskIndex <= 0) {
        this.prevButtonTarget.classList.add('hidden');
      } else {
        this.prevButtonTarget.classList.remove('hidden');
      }
      this.prevButtonTarget.disabled = this.currentMissionTaskIndex <= 0;
    }
    if (this.hasNextButtonTarget) {
      if (this.currentMissionTaskIndex >= this.missionTaskIds.length - 1) {
        this.nextButtonTarget.classList.add('hidden');
      } else {
        this.nextButtonTarget.classList.remove('hidden');
      }
      this.nextButtonTarget.disabled = this.currentMissionTaskIndex >= this.missionTaskIds.length - 1;
    }
  }
}
